<script lang="ts" setup>
import type { estypes } from '@elastic/elasticsearch'
import { fromZonedTime } from 'date-fns-tz'
import sanitizeHtml from 'sanitize-html'
import {
  Employee,
  EmployeeUuidQueryDocument,
  EmployeeUuidQueryQueryVariables,
  MediaImageFragment,
} from '~/api/graphql/generated'
import { Employee as EmployeeData } from '~/views/cms/components/EmployeeSingle.vue'
import { useTypedAsyncQuery } from '~/lib/utils/graphqlDocument'

const props = defineProps<{
  entity: estypes.SearchHit
}>()

const controller = useModal()
const { t } = useI18n()

const type = computed(() =>
  props.entity.fields?.type && ['page', 'landing_page'].includes(props.entity.fields.type[0])
    ? 'webcontent'
    : props.entity.fields?.type[0],
)
const title = computed(() =>
  props.entity.highlight?.title ? props.entity.highlight.title[0] : props.entity.fields?.title[0],
)

const content = props.entity.highlight?.content
  ? props.entity.highlight.content.join(' ... ')
  : props.entity.highlight?.['attachment.content']
    ? props.entity.highlight['attachment.content'].join(' ... ')
    : ''

const url = props.entity.fields?.url ? props.entity.fields.url[0] : false

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
const nowUtc = fromZonedTime(new Date(), tz).toISOString()

const employeeData = ref()

const handleClick = async () => {
  if (!props.entity.fields?.id) return
  const employeeUuid = props.entity.fields.id[0]

  const variables: EmployeeUuidQueryQueryVariables = { uuid: employeeUuid, now: nowUtc }
  const { data } = await useTypedAsyncQuery(EmployeeUuidQueryDocument, variables)

  const employee = data.value?.employeeQuery?.entities?.[0] as Employee

  if (!employee) return
  employeeData.value = {
    id: employee.id,
    name: `${employee.fieldAcademicTitle ?? ''} ${employee.fieldFirstName} ${employee.fieldLastName}`,
    email: employee.fieldEmail ?? '',
    phone: employee.fieldPhone ?? '',
    position: sanitizeHtml(employee.fieldPosition?.[0]?.entity?.fieldWorkTitle ?? ''),
    description: employee.fieldDescription ?? '',
    link: {
      url: employee.entityUrl?.path || '',
      text: t('employee.detail.copyUrlText'),
    },
    image: employee.fieldImage?.entity as MediaImageFragment,
  } as EmployeeData

  controller.open()
}
</script>

<template>
  <li v-animate="{ animation: 'vertical-reveal' }" class="search-result">
    <h6 :type="type" class="type">
      <Icon v-if="type === 'employee'" name="profile" />
      <Icon v-else-if="type === 'document'" name="document" />
      <Icon v-else name="web" />

      {{ t(`search.page.teaser.type.${type}`) }}
    </h6>
    <h3 v-if="title" class="title text-medium" v-html="title" />
    <p v-if="content" class="content text-small" v-html="content" />
    <NuxtLink v-if="type === 'employee'" class="link" @click="handleClick" />
    <NuxtLink v-else :to="url" class="link" />

    <ModalEmployee :controller="controller">
      <EmployeeSingle :data="employeeData" />
    </ModalEmployee>
  </li>
</template>

<style lang="scss" scoped>
.search-result {
  position: relative;

  padding-block: 48px;
  border-bottom: 1px solid var(--c-background-strong);

  display: flex;
  flex-direction: column;
  gap: 16px;

  .type {
    --icon-height: 24px;

    display: flex;
    align-items: center;
    gap: 12px;

    margin-bottom: 0;

    span {
      display: block;
      height: var(--icon-size);
      width: var(--icon-size);
    }
  }

  .title {
    margin-bottom: 0;
  }

  .content {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;

    color: var(--c-text-medium);
    margin-top: 16px;
    margin-bottom: 0;

    :deep(em) {
      color: var(--c-text-dark);
      font-style: normal;
      font-weight: 700;
    }
  }

  .link {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    z-index: 1;
  }
}
</style>
